import { Box, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { ErrorBoundary } from "react-error-boundary"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"
import { UserDashboardErrorFallbackCard } from "../UserDashboardErrorFallbackCard"
import { CARD_HEIGHT, CARD_WIDTH } from "../dashboardCardDimensions"

import type { MyLunchSubscriptionsResponse, OrderedLunchSubscription, SubscriptionKitchen } from "./subscriptionsTypes"
import { fetchMyLunchSubscriptions, UserSubscriptionsQuery } from "./subscriptionsApi"
import { SubscriptionCard } from "./SubscriptionCard"

const useStyles = makeStyles()((theme) => ({
    sectionTitle: {
        fontSize: "1.25rem",
        fontWeight: 500,
        color: theme.palette.text.primary,
        marginBottom: ".5rem",
    },
    row: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: ".95rem",
    },
    skeleton: {
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        borderRadius: ".75rem",
        gridRow: "span 2",
    },
}))

type SubscriptionCardEntry = Readonly<{
    key: string
    order: OrderedLunchSubscription
    kitchen: SubscriptionKitchen
}>

const selectEntries = (data: MyLunchSubscriptionsResponse): SubscriptionCardEntry[] =>
    data.orders.flatMap((order) =>
        order.subscriptionDetails.kitchens.map((kitchen, index) => ({
            key: `${order.id}-${index}`,
            order,
            kitchen,
        })),
    )

export function SubscriptionCards() {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const { data: entries, isLoading } = useApiQuery(fetchMyLunchSubscriptions, {
        queryName: UserSubscriptionsQuery.MY_LUNCH_SUBSCRIPTIONS,
        dependencies: {},
        select: selectEntries,
        refetchInterval: RefetchIntervals.medium,
    })

    if (isLoading) {
        return (
            <Box className={classes.row}>
                <Skeleton variant="rectangular" className={classes.skeleton} />
            </Box>
        )
    }

    const today = dateTimeFactory.now().startOf("day")
    const visibleEntries = entries?.filter(({ order }) => {
        const { unsubscribeDate } = order.subscriptionDetails
        if (!unsubscribeDate) return true
        return dateTimeFactory.fromApi(unsubscribeDate).startOf("day") >= today
    })

    if (!visibleEntries?.length) {
        return null
    }

    return (
        <Box>
            <LocalizedStrict id="user-home-subscriptions-section-title">
                <Typography component="h3" className={classes.sectionTitle}>
                    Subscriptions
                </Typography>
            </LocalizedStrict>
            <Box className={classes.row}>
                {visibleEntries.map(({ key, order, kitchen }) => (
                    <ErrorBoundary key={key} fallback={<UserDashboardErrorFallbackCard />}>
                        <SubscriptionCard
                            lunchSubscription={order.subscriptionDetails.lunchSubscription}
                            kitchen={kitchen}
                            usageDetails={order.subscriptionDetails.usageDetails}
                            subscriptionDate={order.subscriptionDetails.subscriptionDate}
                            unsubscribeDate={order.subscriptionDetails.unsubscribeDate}
                        />
                    </ErrorBoundary>
                ))}
            </Box>
        </Box>
    )
}
